// Pure logic for year-over-year deduction trend reporting.
// Kept free of Preact/recharts imports so it can be unit tested in isolation.

export type Granularity = "year" | "quarter" | "month"
export type Metric = "pct_of_revenue" | "dollars"
export type Dimension = "customer" | "category"

// Synthetic dimension representing all deductions combined ("are we improving overall?").
export const ALL_KEY = "__all__"
export const ALL_LABEL = "All deductions"

/** A monthly data point for a single dimension value (a customer or a category). */
export interface MonthlyDimensionRow {
  /** First-of-month ISO date, e.g. "2025-03-01". */
  month_key: string
  /** The dimension value: customer/source name, or expense account group. */
  dimension: string
  /** Sales attributable to this dimension in the month (0 for categories). */
  total_sales: number
  /** Deductions for this dimension in the month (stored as a positive number). */
  total_deductions: number
}

/** Aggregated sales + deductions for a (period, dimension) pair. */
export interface PeriodDimensionAgg {
  period: string
  dimension: string
  sales: number
  deductions: number
}

export interface ChartRow {
  period: string
  [dimension: string]: number | string | null
}

/**
 * Maps a first-of-month ISO date to its period key.
 * - year: "2025"
 * - quarter: "2025-Q2" (lexicographically sortable in chronological order)
 */
export function period_key(month_key: string, granularity: Granularity): string {
  const [year, month] = month_key.split("-")
  if (granularity === "year") return year
  if (granularity === "month") return `${year}-${month}`
  const month_num = Number(month)
  const quarter = Math.floor((month_num - 1) / 3) + 1
  return `${year}-Q${quarter}`
}

/**
 * Returns the comparable period one year earlier:
 * "2025" -> "2024", "2025-Q2" -> "2024-Q2", "2025-03" -> "2024-03".
 */
export function prior_year_period(period: string, granularity: Granularity): string {
  if (granularity === "year") return String(Number(period) - 1)
  const [year, rest] = period.split("-")
  return `${Number(year) - 1}-${rest}`
}

function pad2(n: number): string {
  return String(n).padStart(2, "0")
}

function iso_local(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

/** Inclusive [start, end] ISO (yyyy-MM-dd) calendar bounds for a period key. */
export function period_bounds(period: string, granularity: Granularity): { start: string; end: string } {
  if (granularity === "year") {
    const y = Number(period)
    return { start: `${y}-01-01`, end: `${y}-12-31` }
  }
  if (granularity === "quarter") {
    const [ys, qs] = period.split("-Q")
    const y = Number(ys)
    const q = Number(qs)
    const start_month = (q - 1) * 3 // 0-based month index
    return { start: iso_local(new Date(y, start_month, 1)), end: iso_local(new Date(y, start_month + 3, 0)) }
  }
  // month "yyyy-MM"
  const [ys, ms] = period.split("-")
  const y = Number(ys)
  const m = Number(ms)
  return { start: iso_local(new Date(y, m - 1, 1)), end: iso_local(new Date(y, m, 0)) }
}

/**
 * Combined inclusive date range spanning two period keys (in any order),
 * e.g. range_from_periods("2024-Q4", "2025-Q1", "quarter") ->
 * { start: "2024-10-01", end: "2025-03-31" }.
 */
export function range_from_periods(p1: string, p2: string, granularity: Granularity): { start: string; end: string } {
  const [lo, hi] = [p1, p2].sort((a, b) => a.localeCompare(b))
  return { start: period_bounds(lo, granularity).start, end: period_bounds(hi, granularity).end }
}

/**
 * Aggregates monthly rows up to the requested granularity, summing sales and
 * deductions per (period, dimension). Also emits a synthetic ALL_KEY dimension
 * per period that totals every dimension, so callers can show an overall line.
 */
export function aggregate_to_periods(
  rows: MonthlyDimensionRow[],
  granularity: Granularity
): PeriodDimensionAgg[] {
  const by_key: Record<string, PeriodDimensionAgg> = {}

  function bump(period: string, dimension: string, sales: number, deductions: number) {
    const key = `${period}|${dimension}`
    if (!by_key[key]) by_key[key] = { period, dimension, sales: 0, deductions: 0 }
    by_key[key].sales += sales
    by_key[key].deductions += deductions
  }

  for (const row of rows) {
    const period = period_key(row.month_key, granularity)
    bump(period, row.dimension, row.total_sales, row.total_deductions)
    bump(period, ALL_KEY, row.total_sales, row.total_deductions)
  }

  return Object.values(by_key)
}

/** Orders dimension values by total deductions across all periods, descending. */
export function order_dimensions_by_deductions(rows: MonthlyDimensionRow[]): string[] {
  const totals: Record<string, number> = {}
  for (const row of rows) {
    totals[row.dimension] = (totals[row.dimension] || 0) + Math.abs(row.total_deductions)
  }
  return Object.entries(totals)
    .sort((a, b) => b[1] - a[1])
    .map(([dimension]) => dimension)
}

/** Total revenue per period, summed across every dimension (used for category % of revenue). */
export function period_revenue_map(
  revenue_rows: MonthlyDimensionRow[],
  granularity: Granularity
): Record<string, number> {
  const out: Record<string, number> = {}
  for (const row of revenue_rows) {
    const period = period_key(row.month_key, granularity)
    out[period] = (out[period] || 0) + row.total_sales
  }
  return out
}

export interface BuildOpts {
  metric: Metric
  /**
   * How to normalize % of revenue:
   * - "per_dimension": deductions / that dimension's own sales (customers)
   * - "period_total": deductions / the period's total revenue (categories)
   */
  revenue_basis: "per_dimension" | "period_total"
  /** Required when revenue_basis === "period_total". */
  period_revenue?: Record<string, number>
  /** [period][dimension] -> repayment dollars, used when net_repayments is on. */
  repayments?: Record<string, Record<string, number>>
  /** When true, subtract repayments from deductions ("true" post-repayment rate). */
  net_repayments?: boolean
}

/** Repayment dollars to subtract for an aggregate (0 unless netting is enabled). */
function repayment_for(agg: PeriodDimensionAgg, opts: BuildOpts): number {
  if (!opts.net_repayments) return 0
  return opts.repayments?.[agg.period]?.[agg.dimension] ?? 0
}

/** Deductions after (optionally) subtracting repayments. */
export function effective_deductions(agg: PeriodDimensionAgg, opts: BuildOpts): number {
  return agg.deductions - repayment_for(agg, opts)
}

/** Computes a single cell value (the metric) for one aggregate. Null when undefined. */
export function metric_value(agg: PeriodDimensionAgg, opts: BuildOpts): number | null {
  const eff = effective_deductions(agg, opts)
  if (opts.metric === "dollars") return eff
  // pct_of_revenue
  const denom =
    opts.revenue_basis === "period_total"
      ? (opts.period_revenue?.[agg.period] ?? 0)
      : agg.sales
  if (!denom) return null
  return (eff / Math.abs(denom)) * 100
}

/** Flattens aggregates into a [period][dimension] -> deductions lookup (also used for repayments). */
export function build_amount_lookup(aggs: PeriodDimensionAgg[]): Record<string, Record<string, number>> {
  const out: Record<string, Record<string, number>> = {}
  for (const agg of aggs) {
    if (!out[agg.period]) out[agg.period] = {}
    out[agg.period][agg.dimension] = agg.deductions
  }
  return out
}

/**
 * Pivots aggregates into chart rows keyed by period, with one column per
 * selected dimension, in chronological period order.
 */
export function build_trend_chart_rows(
  aggs: PeriodDimensionAgg[],
  selected_dimensions: string[],
  opts: BuildOpts
): ChartRow[] {
  const selected = new Set(selected_dimensions)
  const by_period: Record<string, ChartRow> = {}

  for (const agg of aggs) {
    if (!selected.has(agg.dimension)) continue
    if (!by_period[agg.period]) by_period[agg.period] = { period: agg.period }
    by_period[agg.period][agg.dimension] = metric_value(agg, opts)
  }

  return Object.values(by_period).sort((a, b) => a.period.localeCompare(b.period))
}

export interface PeriodDimensionDetail {
  /** Gross deduction dollars. */
  deductions: number
  /** Repayment dollars subtracted when netting is on (0 otherwise). */
  repayments: number
  /** Deductions actually charted: gross, or gross minus repayments when netting. */
  effective: number
  /** The revenue dollars used as the % -of-revenue denominator for this point. */
  revenue: number
  /** effective / revenue * 100, or null when revenue is zero. */
  pct: number | null
}

/** Computes the underlying dollar figures behind a single aggregate's metric. */
export function detail_for(agg: PeriodDimensionAgg, opts: BuildOpts): PeriodDimensionDetail {
  const revenue =
    opts.revenue_basis === "period_total" ? (opts.period_revenue?.[agg.period] ?? 0) : agg.sales
  const repayments = repayment_for(agg, opts)
  const effective = agg.deductions - repayments
  const pct = revenue ? (effective / Math.abs(revenue)) * 100 : null
  return { deductions: agg.deductions, repayments, effective, revenue, pct }
}

/**
 * Nested lookup [period][dimension] -> the dollar detail behind each chart point,
 * so a tooltip can show what's driving the percentage.
 */
export function build_detail_lookup(
  aggs: PeriodDimensionAgg[],
  opts: BuildOpts
): Record<string, Record<string, PeriodDimensionDetail>> {
  const out: Record<string, Record<string, PeriodDimensionDetail>> = {}
  for (const agg of aggs) {
    if (!out[agg.period]) out[agg.period] = {}
    out[agg.period][agg.dimension] = detail_for(agg, opts)
  }
  return out
}

export interface YoYDelta {
  dimension: string
  current_period: string
  current_value: number | null
  prior_period: string
  prior_value: number | null
  /** current_value - prior_value, when both are present. */
  delta: number | null
}

/**
 * For each selected dimension, compares its most recent period that has data
 * against the comparable period one year earlier.
 */
export function compute_yoy_deltas(
  chart_rows: ChartRow[],
  selected_dimensions: string[],
  granularity: Granularity
): YoYDelta[] {
  const by_period: Record<string, ChartRow> = {}
  for (const row of chart_rows) by_period[row.period] = row

  return selected_dimensions.map((dimension) => {
    // Latest period (chronologically) where this dimension has a value.
    const periods_with_data = chart_rows
      .filter((row) => row[dimension] != null)
      .map((row) => row.period)
      .sort((a, b) => a.localeCompare(b))
    const current_period = periods_with_data[periods_with_data.length - 1] ?? ""
    const prior_period = current_period ? prior_year_period(current_period, granularity) : ""

    const current_value = (by_period[current_period]?.[dimension] as number | null | undefined) ?? null
    const prior_value = (by_period[prior_period]?.[dimension] as number | null | undefined) ?? null
    const delta = current_value != null && prior_value != null ? current_value - prior_value : null

    return { dimension, current_period, current_value, prior_period, prior_value, delta }
  })
}
